/**
 * Admin Bypass Utility
 * 
 * Single source of truth for admin testing mode across the frontend.
 * Use this utility to check if a user should bypass credit restrictions.
 */

// Approved admin emails that bypass credit checks
const ADMIN_EMAILS = [
  'swinland@gmail.com',
  'admin@mockuppro.org',
  'testadmin@mockuppro.org'
];

/**
 * Check if a user should bypass credit restrictions
 * @param {Object} user - User object from AuthContext
 * @returns {boolean} True if user is an admin tester
 */
export const isAdminTester = (user) => {
  if (!user) return false;
  
  // Check is_admin flag from backend
  if (user.is_admin === true) return true;
  
  // Check email against approved list
  const email = user.email?.toLowerCase();
  if (email && ADMIN_EMAILS.includes(email)) return true;
  
  return false;
};

/**
 * Check if an action should proceed for a user based on credits
 * Returns true if user has enough credits OR is an admin
 * 
 * @param {Object} user - User object from AuthContext
 * @param {number} requiredCredits - Credits required for the action
 * @returns {boolean} True if action should proceed
 */
export const shouldBypassCreditCheck = (user, requiredCredits = 0) => {
  if (isAdminTester(user)) {
    console.log('[AdminBypass] Bypassing credit check for admin:', user?.email);
    return true;
  }
  
  const userCredits = user?.credits || 0;
  return userCredits >= requiredCredits;
};

/**
 * Log admin bypass status for debugging
 * @param {string} component - Component name
 * @param {string} action - Action being performed
 * @param {Object} user - User object
 * @param {boolean} bypassed - Whether bypass was applied
 */
export const logAdminBypass = (component, action, user, bypassed) => {
  if (process.env.NODE_ENV !== 'production') {
    console.log(`[AdminBypass] ${component}:${action}`, {
      email: user?.email,
      is_admin: user?.is_admin,
      bypassed,
      credits: user?.credits
    });
  }
};

/**
 * Get credit display for UI
 * Shows "Admin" for admin users instead of credit count
 * @param {Object} user - User object
 * @returns {string} Display text for credits
 */
export const getCreditDisplay = (user) => {
  if (isAdminTester(user)) {
    return 'Admin';
  }
  return Math.max(0, user?.credits || 0).toString();
};

export default {
  isAdminTester,
  shouldBypassCreditCheck,
  logAdminBypass,
  getCreditDisplay,
  ADMIN_EMAILS
};
